import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { checkEligibility } from '../../services/eligibilityService';
import Modal from '../common/Modal';
import PayerCombobox from './PayerCombobox';

const schema = z.object({
  payerName: z.string().min(1, 'Payer name is required'),
  payerId: z.string().optional().or(z.literal('')),
  subscriberId: z.string().optional().or(z.literal('')),
  serviceDate: z.string().optional().or(z.literal('')),
});

const fmt = (val) =>
  val != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
    : '—';

function BenefitRow({ label, value }) {
  if (value == null) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-neutral-50 dark:border-neutral-700/40 last:border-0">
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{value}</span>
    </div>
  );
}

function EligibilityResult({ check }) {
  const isActive = check.isActive;

  return (
    <div className="mt-5 space-y-4">
      <div className={`rounded-lg px-4 py-3 ${isActive ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
        <p className={`font-semibold ${isActive ? 'text-green-800 dark:text-green-300' : 'text-danger-700 dark:text-danger-400'}`}>
          {isActive ? 'Coverage is active' : 'Coverage is inactive or not found'}
        </p>
        {check.planName && (
          <p className="text-sm mt-0.5 text-neutral-600 dark:text-neutral-400">{check.planName}</p>
        )}
      </div>

      {isActive && (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700/50 px-4">
          <BenefitRow label="Group number" value={check.groupNumber} />
          <BenefitRow label="Coverage start" value={check.coverageStartDate} />
          <BenefitRow label="Coverage end" value={check.coverageEndDate} />
          <BenefitRow label="Deductible (individual)" value={fmt(check.deductibleIndividual)} />
          <BenefitRow
            label="Deductible remaining"
            value={check.deductibleIndividualRemaining != null ? fmt(check.deductibleIndividualRemaining) : null}
          />
          <BenefitRow label="Deductible (family)" value={fmt(check.deductibleFamily)} />
          <BenefitRow label="Out-of-pocket max" value={fmt(check.outOfPocketIndividual)} />
          <BenefitRow
            label="Out-of-pocket remaining"
            value={check.outOfPocketIndividualRemaining != null ? fmt(check.outOfPocketIndividualRemaining) : null}
          />
          <BenefitRow label="Copay" value={check.copayAmount != null ? fmt(check.copayAmount) : null} />
          <BenefitRow
            label="Coinsurance"
            value={check.coinsurancePct != null ? `${check.coinsurancePct}%` : null}
          />
        </div>
      )}

      {check.errorMessage && (
        <p className="text-sm text-danger-600 dark:text-danger-400">{check.errorMessage}</p>
      )}
    </div>
  );
}

export default function EligibilityCheckModal({ isOpen, onClose, clientId, appointmentId, defaultPayerName, defaultPayerId, defaultSubscriberId }) {
  const { register, handleSubmit, setValue, control, formState: { errors }, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      payerName: defaultPayerName || '',
      payerId: defaultPayerId || '',
      subscriberId: defaultSubscriberId || '',
      serviceDate: '',
    },
  });

  const { field: payerNameField } = useController({ name: 'payerName', control });
  const { field: payerIdField } = useController({ name: 'payerId', control });

  const handlePayerSelect = ({ name, ediId }) => {
    setValue('payerName', name, { shouldValidate: true });
    if (ediId != null) setValue('payerId', ediId);
  };

  const mutation = useMutation({
    mutationFn: (data) => checkEligibility({ ...data, clientId, appointmentId }),
    onError: (err) => toast.error(err.response?.data?.message || 'Eligibility check failed'),
  });

  const handleClose = () => {
    mutation.reset();
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Check Eligibility & Benefits" size="md">
      {!mutation.isSuccess ? (
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payer *
            </label>
            <PayerCombobox
              value={payerNameField.value}
              onChange={handlePayerSelect}
              error={errors.payerName}
            />
            {errors.payerName && (
              <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.payerName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Payer EDI ID
              </label>
              <input
                type="text"
                className="input font-mono"
                placeholder="e.g. 60054"
                {...payerIdField}
              />
              <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">Auto-filled when payer is selected</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Member / Subscriber ID
              </label>
              <input type="text" className="input" placeholder="e.g. XYZ123456" {...register('subscriberId')} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Service date <span className="text-neutral-400 font-normal">(optional)</span>
            </label>
            <input type="date" className="input" {...register('serviceDate')} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={handleClose} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {mutation.isPending ? 'Checking…' : 'Check eligibility'}
            </button>
          </div>
        </form>
      ) : (
        <div>
          <EligibilityResult check={mutation.data.check} />
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => mutation.reset()} className="btn btn-secondary">
              Check again
            </button>
            <button onClick={handleClose} className="btn btn-primary">
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
